import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, Shield, Wrench, Bell, LifeBuoy, Clock, Check, ArrowRight, Lock, Zap, Eye, RefreshCw, Database, Target, ShieldAlert, Activity, Flame, Globe } from "lucide-react";
import { Helmet } from "@/components/Helmet";

const Services = () => {
  const services = [
    {
      icon: Zap,
      code: "MITIGATE",
      title: "Threat Detection and Containment",
      description:
        "Detects suspicious behaviour and applies safe, reversible containment when thresholds are exceeded.",
      features: [
        "Detects repeated login abuse and access spikes",
        "Contains activity with reversible lockouts and throttling",
        "Validates high-impact signals with human review",
        "Summaries prioritised by impact and exposure",
        "Application-level containment - no edge protection required",
      ],
    },
    {
      icon: Eye,
      code: "INTELLIGENCE",
      title: "Vulnerability Intelligence",
      description:
        "Tracks disclosures, exploitability, and exposure across your stack.",
      features: [
        "Monitors new core, plugin, and theme disclosures",
        "Prioritises risk by exploitability and usage",
        "Maps findings to your installed software",
        "Early visibility before public disclosure where possible",
        "Advisories translated into ShadowScan remediation steps",
      ],
    },
    {
      icon: Shield,
      code: "PROTECTION",
      title: "Application-Level Protections",
      description:
        "Reduces common WordPress attack paths while keeping sites stable.",
      features: [
        "Reduces exposed entry points like XML-RPC and enumeration",
        "Safe, reversible hardening toggles",
        "Login abuse containment with last-admin safety",
        "Configuration hygiene checks and recommendations",
        "Works alongside customer-owned edge protection",
      ],
    },
    {
      icon: Activity,
      code: "MONITORING",
      title: "Continuous Monitoring",
      description:
        "Signals, validation, and escalation with human oversight.",
      features: [
        "Monitors integrity changes and suspicious file activity",
        "Validates alerts to reduce noise",
        "Centralised reporting across monitored sites",
        "Incident timeline and evidence summaries",
        "Continuous monitoring with human validation",
      ],
    },
    {
      icon: RefreshCw,
      code: "UPDATES",
      title: "Recovery and Hardening",
      description:
        "Guided remediation and safe updates that reduce future exposure.",
      features: [
        "Guides safe core, plugin, and theme updates",
        "Prioritises remediation of high-risk items",
        "Post-incident hardening checkpoints",
        "Recovery coordination and follow-up verification",
        "Operational notes and runbooks",
      ],
    },
    {
      icon: LifeBuoy,
      code: "SOC_RESPONSE",
      title: "SOC Support (Premium)",
      description:
        "Analyst-led monitoring with incident ownership and remediation.",
      features: [
        "Active analyst monitoring and validation",
        "Containment and stabilization actions coordinated",
        "Remediation performed by ShadowScan for Premium",
        "Post-incident review and tuning",
        "Dedicated account management for portfolios",
      ],
    },
  ];

  const faqItems = [
    {
      question: "Do you include a firewall or WAF?",
      answer:
        "ShadowScan does not embed a firewall into WordPress. We focus on application-level protections, continuous monitoring, and rapid human response. If you already use a WAF or edge protection, ShadowScan works alongside it.",
    },
    {
      question: "What is included in ShadowScan's managed WordPress security service?",
      answer:
        "We provide managed WordPress security including continuous monitoring, application-level protections, threat detection and containment, integrity and admin monitoring, and ongoing guidance.",
    },
    {
      question: "Do you offer 24/7 WordPress security monitoring?",
      answer:
        "Yes. Our team monitors sites around the clock with validated alerts and human-led response when suspicious activity is detected.",
    },
    {
      question: "What happens if my WordPress site is compromised?",
      answer:
        "We coordinate containment and recovery, identify root causes, restore site integrity, and recommend hardening steps to reduce future risk.",
    },
    {
      question: "How does ShadowScan reduce risk without a WAF?",
      answer:
        "ShadowScan focuses on application-level protections inside WordPress, continuous monitoring, and safe containment of suspicious behaviour. If you already use customer-owned edge protection, ShadowScan works alongside it."
    },
  ];

  const featureGroups = [
    {
      title: "Monitoring",
      items: [
        "Continuous monitoring with validated alerts",
        "Vulnerability intelligence with risk prioritisation",
        "Connector health and site inventory tracking",
        "Centralised reporting across monitored sites",
      ],
    },
    {
      title: "Protection Controls",
      items: [
        "Application-level protections with human oversight",
        "Attack surface reduction guidance",
        "Login abuse containment and access control improvements",
        "Safe update workflows (core, plugins, themes)",
      ],
    },
    {
      title: "Incident Response",
      items: [
        "Human-led triage and response coordination",
        "Response playbooks aligned to severity",
        "Priority incident response for Premium",
        "Clear remediation guidance",
      ],
    },
    {
      title: "Recovery & Hardening",
      items: [
        "Malware cleanup coordination",
        "Root-cause analysis and recovery planning",
        "Post-incident review and hardening",
        "Ongoing security optimisation",
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>ShadowScan Services | Managed WordPress Security</title>
        <meta
          name="description"
          content="Managed WordPress security services: continuous monitoring, application-level protections, threat detection and containment, and optional Premium SOC response."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What is included in ShadowScan's managed WordPress security service?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "ShadowScan provides managed WordPress security including continuous monitoring, application-level protections, threat detection and containment, integrity and admin monitoring, and ongoing guidance."
                }
              },
              {
                "@type": "Question",
                name: "How does ShadowScan reduce risk on WordPress sites?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We combine continuous monitoring, application-level protections, and safe containment with human validation from our Security Operations Center."
                }
              },
              {
                "@type": "Question",
                name: "Do you include a firewall or WAF?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "ShadowScan does not embed a firewall into WordPress. We focus on application-level protections, continuous monitoring, and rapid human response. If you already use a WAF or edge protection, ShadowScan works alongside it."
                }
              },
              {
                "@type": "Question",
                name: "Do you offer 24/7 WordPress security monitoring?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes, ShadowScan provides round-the-clock monitoring with validated alerts and human-led response when suspicious activity is detected."
                }
              },
              {
                "@type": "Question",
                name: "What happens if my WordPress site is compromised?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We coordinate containment and recovery, identify root causes, restore site integrity, and recommend hardening steps to reduce future risk."
                }
              },
              {
                "@type": "Question",
                name: "How often do you update WordPress plugins and themes?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We manage all WordPress core, theme, and plugin updates as part of our service. Updates are tested for compatibility before deployment to ensure your site remains stable and secure."
                }
              },
              {
                "@type": "Question",
                name: "What is the difference between Essential and Premium security plans?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Essential (AUD $50/site/month) provides continuously monitored WordPress security with vulnerability intelligence and validated response guidance. Premium (AUD $185/site/month) adds analyst-led monitoring, incident ownership, and remediation."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      <Navigation />
      <main className="flex-grow pt-24">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-6">
                <span className="font-mono text-sm text-primary">// MANAGED_SECURITY</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                Managed <span className="text-gradient">WordPress Security</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Managed WordPress security with continuous monitoring, attack surface reduction, and rapid human response - plus guided updates and optional SOC response.
              </p>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <Card key={index} className="cyber-card border-border hover:border-primary/50 transition-all duration-300 group">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-primary/10 border border-primary/30 rounded-lg flex items-center justify-center">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <span className="font-mono text-xs text-primary/60 bg-primary/5 px-2 py-1 rounded">{service.code}</span>
                      </div>
                      <CardTitle className="text-lg font-heading">{service.title}</CardTitle>
                      <CardDescription className="text-sm">{service.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {service.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="font-mono text-sm text-primary mb-4">// FEATURE_GROUPS</div>
              <h2 className="text-3xl font-heading font-bold mb-4">What You Get</h2>
              <p className="text-muted-foreground">
                Structured coverage across monitoring, protection controls, incident response, and recovery.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {featureGroups.map((group) => (
                <Card key={group.title} className="cyber-card border-border/60">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{group.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {group.items.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="font-mono text-sm text-primary mb-4">// INCIDENT_FLOW</div>
              <h2 className="text-3xl font-heading font-bold mb-4">What Happens During an Incident</h2>
              <p className="text-muted-foreground">
                Detect - contain - validate - respond, with clear outcomes for each plan.
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-4 max-w-5xl mx-auto mb-8">
              {[
                { title: "Detect", detail: "Suspicious behaviour and integrity changes are flagged." },
                { title: "Contain", detail: "Safe, reversible controls limit impact." },
                { title: "Validate", detail: "Analysts verify high-risk signals before escalation." },
                { title: "Respond", detail: "Clear actions and remediation are coordinated." },
              ].map((step) => (
                <Card key={step.title} className="cyber-card border-border/60">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{step.detail}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <Card className="cyber-card border-border/60">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Essential Outcome</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    ShadowScan contains suspicious activity automatically, validates high-risk alerts, and provides clear remediation steps for your team to execute.
                  </p>
                </CardContent>
              </Card>
              <Card className="cyber-card border-primary/40">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Premium Outcome</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    ShadowScan takes incident ownership - we validate, contain, remediate, and coordinate recovery to stabilize the site quickly.
                  </p>
                </CardContent>
              </Card>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-8">
              No security service can eliminate risk entirely. ShadowScan reduces risk, detects issues early, and responds quickly to limit impact.
            </p>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="font-mono text-sm text-primary mb-4">// FAQ</div>
              <h2 className="text-3xl font-heading font-bold mb-4">Frequently Asked Questions</h2>
              <p className="text-muted-foreground">
                Straight answers on what ShadowScan provides and how we work.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {faqItems.map((item) => (
                <Card key={item.question} className="cyber-card border-border/60">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{item.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{item.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 relative">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center cyber-card rounded-2xl p-12 border-primary/30">
              <div className="font-mono text-sm text-primary mb-4">// GET_STARTED</div>
              <h2 className="text-3xl font-heading font-bold mb-4">Ready to Secure Your Site?</h2>
              <p className="text-muted-foreground mb-8">Get started with managed WordPress security today</p>
              <Button size="lg" asChild className="bg-accent hover:bg-accent/90 text-accent-foreground">
                <Link to="/contact"><Lock className="mr-2 h-5 w-5" />Get Started Now<ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Services;
